import React, { useEffect, useState } from 'react';
import { Icon } from '../Icon';

export const AboutPage: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [stats, setStats] = useState({ calcs: 0, millions: 0 });

  useEffect(() => {
    // Simple number animation
    let currentCalcs = 0;
    let currentMillions = 0;
    const interval = setInterval(() => {
      if (currentCalcs < 50) currentCalcs += 2;
      if (currentMillions < 1) currentMillions += 0.05;

      setStats({
        calcs: Math.min(currentCalcs, 50),
        millions: Math.min(currentMillions, 1)
      });

      if (currentCalcs >= 50 && currentMillions >= 1) {
        clearInterval(interval);
      }
    }, 40);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-theme-bg flex flex-col font-inter transition-colors duration-300">
      <nav className="bg-theme-card border-b border-theme-border px-6 py-4 sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button
            onClick={onBack}
            className="group flex items-center gap-2.5 text-base font-bold text-theme-muted hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
          >
            <Icon
              name="arrow-left"
              className="w-5 h-5 transform group-hover:-translate-x-1.5 transition-transform"
            />
            Voltar ao Hub
          </button>
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-black px-4 py-2 rounded-xl uppercase tracking-widest shadow-sm bg-indigo-50 text-indigo-600 dark:bg-slate-800 dark:text-indigo-400">
              Sobre Nós
            </span>
          </div>
        </div>
      </nav>

      <main className="flex-grow max-w-5xl mx-auto w-full px-6 py-12">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-[1000] text-theme-text mb-4 tracking-tight">Sobre Nós</h1>
          <p className="text-theme-muted font-medium text-lg max-w-2xl mx-auto">
            Conheça a história e missão do Calculadora PRO
          </p>
        </div>

        {/* Missão */}
        <section className="bg-theme-card rounded-3xl shadow-sm border border-theme-border p-8 md:p-12 mb-8 text-center">
          <h2 className="text-3xl font-[1000] text-theme-text mb-4">🚀 Nossa Missão</h2>
          <p className="text-lg text-theme-muted leading-relaxed max-w-3xl mx-auto">
            Democratizar o acesso a ferramentas matemáticas e financeiras de alta qualidade,
            oferecendo calculadoras precisas, gratuitas e fáceis de usar para todos os brasileiros.
          </p>
        </section>

        {/* Valores */}
        <section className="bg-theme-card rounded-3xl shadow-sm border border-theme-border p-8 md:p-12 mb-8">
          <h2 className="text-3xl font-[1000] text-theme-text mb-10 text-center">💎 Nossos Valores</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-theme-bg p-8 rounded-2xl text-center border border-theme-border hover:-translate-y-1 transition-transform">
              <div className="w-14 h-14 bg-indigo-50 dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 rounded-2xl flex items-center justify-center mx-auto mb-4 text-3xl shadow-sm">
                🎯
              </div>
              <h3 className="font-bold text-theme-text text-lg mb-2">Qualidade</h3>
              <p className="text-theme-muted text-sm leading-relaxed">Cálculos validados e testados para garantir resultados confiáveis.</p>
            </div>
            <div className="bg-theme-bg p-8 rounded-2xl text-center border border-theme-border hover:-translate-y-1 transition-transform">
              <div className="w-14 h-14 bg-indigo-50 dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 rounded-2xl flex items-center justify-center mx-auto mb-4 text-3xl shadow-sm">
                🌍
              </div>
              <h3 className="font-bold text-theme-text text-lg mb-2">Acessibilidade</h3>
              <p className="text-theme-muted text-sm leading-relaxed">Ferramentas gratuitas para todos, sem barreiras ou limitações.</p>
            </div>
            <div className="bg-theme-bg p-8 rounded-2xl text-center border border-theme-border hover:-translate-y-1 transition-transform">
              <div className="w-14 h-14 bg-indigo-50 dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 rounded-2xl flex items-center justify-center mx-auto mb-4 text-3xl shadow-sm">
                📱
              </div>
              <h3 className="font-bold text-theme-text text-lg mb-2">Simplicidade</h3>
              <p className="text-theme-muted text-sm leading-relaxed">Interface intuitiva que qualquer pessoa pode usar facilmente.</p>
            </div>
            <div className="bg-theme-bg p-8 rounded-2xl text-center border border-theme-border hover:-translate-y-1 transition-transform">
              <div className="w-14 h-14 bg-indigo-50 dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 rounded-2xl flex items-center justify-center mx-auto mb-4 text-3xl shadow-sm">
                🔒
              </div>
              <h3 className="font-bold text-theme-text text-lg mb-2">Privacidade</h3>
              <p className="text-theme-muted text-sm leading-relaxed">Seus dados permanecem privados. Não coletamos dados pessoais.</p>
            </div>
          </div>
        </section>

        {/* Estatísticas */}
        <section className="bg-accent-gradient text-white rounded-3xl p-10 md:p-14 mb-8 shadow-accent">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-[1000]">📊 Nossos Números</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl md:text-5xl font-black mb-2">{Math.floor(stats.calcs)}+</div>
              <p className="text-indigo-100 font-medium text-sm">Calculadoras Disponíveis</p>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-black mb-2">{stats.millions >= 1 ? '1M+' : `${Math.floor(stats.millions * 1000)}k`}</div>
              <p className="text-indigo-100 font-medium text-sm">Cálculos Realizados</p>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-black mb-2">100%</div>
              <p className="text-indigo-100 font-medium text-sm">Gratuito e Sem Anúncios</p>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-black mb-2">24/7</div>
              <p className="text-indigo-100 font-medium text-sm">Disponibilidade</p>
            </div>
          </div>
        </section>

        {/* História */}
        <section className="bg-theme-card rounded-3xl shadow-sm border border-theme-border p-8 md:p-12 mb-8">
          <h2 className="text-3xl font-[1000] text-theme-text mb-10 text-center">📅 Nossa História</h2>
          <div className="space-y-6 max-w-3xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4 md:gap-8 items-start bg-theme-bg p-6 rounded-2xl border border-theme-border">
              <div className="font-black text-2xl text-indigo-500 min-w-[80px]">2023</div>
              <div>
                <h3 className="text-xl font-bold text-theme-text mb-2">🌱 Início</h3>
                <p className="text-theme-muted leading-relaxed">O Calculadora PRO nasceu da necessidade de oferecer ferramentas matemáticas confiáveis e gratuitas para estudantes e profissionais.</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-4 md:gap-8 items-start bg-theme-bg p-6 rounded-2xl border border-theme-border">
              <div className="font-black text-2xl text-indigo-500 min-w-[80px]">2024</div>
              <div>
                <h3 className="text-xl font-bold text-theme-text mb-2">🚀 Crescimento</h3>
                <p className="text-theme-muted leading-relaxed">Expansão para mais de 50 calculadoras, cobrindo finanças, saúde, trabalho e matemática básica.</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-4 md:gap-8 items-start bg-theme-bg p-6 rounded-2xl border border-theme-border">
              <div className="font-black text-2xl text-indigo-500 min-w-[80px]">2025</div>
              <div>
                <h3 className="text-xl font-bold text-theme-text mb-2">🎯 Inovação</h3>
                <p className="text-theme-muted leading-relaxed">Implementação de testes automatizados e validação matemática para garantir qualidade em todos os cálculos.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Tecnologias */}
        <section className="bg-theme-card rounded-3xl shadow-sm border border-theme-border p-8 md:p-12">
          <h2 className="text-3xl font-[1000] text-theme-text mb-10 text-center">⚙️ Tecnologias</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-theme-bg p-6 rounded-2xl text-center border border-theme-border">
              <div className="text-4xl mb-3">⚛️</div>
              <h3 className="font-bold text-theme-text text-lg mb-2">React</h3>
              <p className="text-theme-muted text-sm leading-relaxed">Interface moderna com excelente experiência.</p>
            </div>
            <div className="bg-theme-bg p-6 rounded-2xl text-center border border-theme-border">
              <div className="text-4xl mb-3">🎨</div>
              <h3 className="font-bold text-theme-text text-lg mb-2">Tailwind CSS</h3>
              <p className="text-theme-muted text-sm leading-relaxed">Design limpo, profissional e responsivo.</p>
            </div>
            <div className="bg-theme-bg p-6 rounded-2xl text-center border border-theme-border">
              <div className="text-4xl mb-3">🧮</div>
              <h3 className="font-bold text-theme-text text-lg mb-2">TypeScript</h3>
              <p className="text-theme-muted text-sm leading-relaxed">Segurança e precisão nos cálculos.</p>
            </div>
            <div className="bg-theme-bg p-6 rounded-2xl text-center border border-theme-border">
              <div className="text-4xl mb-3">📱</div>
              <h3 className="font-bold text-theme-text text-lg mb-2">PWA</h3>
              <p className="text-theme-muted text-sm leading-relaxed">App web progressivo que funciona offline.</p>
            </div>
          </div>
        </section>
      </main>

      <footer className="py-12 border-t border-theme-border text-center mt-auto">
        <p className="text-[10px] font-black text-theme-muted uppercase tracking-[0.3em]">
          Calculadora PRO • Ferramentas Úteis v4.0
        </p>
      </footer>
    </div>
  );
};
